import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils, comnEnvs } from "@/comn/utils";
import { Page, Group, Layout, Button, Grid } from "@/comn/components";
import { useForm, useFetch, useStore, useToast, useModal, useResource, useGrid, usePage, useTheme } from "@/comn/hooks";
import { Link } from "react-router-dom";
import {
    SF_REVW_PRCSS_SRCH,
    SG_REVW_PRCSS_LIST,
    URLS,
    APIS,
} from "@/tra/tancis/ptli/ptl/revwprcss/services/PtliRevwPrcssService";

/**
 * Review Process List Page
 * ------------------------
 * Displays a paginated list of review processes with search and navigation functionality.
 *
 * Form:
 * - Uses SF_REVW_PRCSS_SRCH schema for filtering by process ID and name.
 * - Includes reset and search buttons with validation feedback.
 *
 * Grid:
 * - Uses SG_REVW_PRCSS_LIST schema.
 * - Displays process ID, name, classification, registrar, and registration date.
 * - Process ID is rendered as a clickable link navigating to the detail/edit page.
 *
 * API:
 * - Fetches paginated review process list using getRevwPrcssList.
 * - Stores form and pagination state in local store for persistence.
 *
 * Handlers:
 * - getRevwPrcssList: triggered on page load or search submission.
 * - onSrchBtnClick: executes search with validation and resets pagination.
 *
 * Navigation:
 * - "New" button navigates to the review process creation page.
 *
 * Lifecycle:
 * - Fetches review process list on component mount.
 */

export const PTLI0707001Q = (props: any) => {
    const { condData, callback, cstmRoleList } = props;
    const { pgeUid, menuId } = usePage({ 
        pgeUid: "ptli0707001q",
        auth: ["auth_ptli0019"] 
    });
    const modal = useModal();
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid });
    const { theme } = useTheme();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const toast = useToast();

    // Load common codes/resources used in this page
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0011" },
            { area: "comnCd", comnCd: "COM0024" },
        ],
    });

    // Search form
    const form = {
        revwPrcssSrch: useForm({
            defaultSchema: SF_REVW_PRCSS_SRCH,
            defaultValues: { ...pgeStore?.form } || {},
        }),
    };

    // Grid for displaying Review Process list
    const grid = {
        revwPrcssList: useGrid({
            defaultSchema: SG_REVW_PRCSS_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    // API fetch for list
    const fetch = {
        getRevwPrcssList: useFetch({
            api: (page = grid.revwPrcssList.page) => {
                return APIS.getRevwPrcssList(form.revwPrcssSrch.getValues(), page, grid.revwPrcssList.size);
            },
            enabled: comnUtils.isEmpty(form.revwPrcssSrch.errors) && form.revwPrcssSrch.isSubmitted,
            key: [grid.revwPrcssList.page, grid.revwPrcssList.size],
            onSuccess: () => {
                // Save form state + pagination state in store
                setStore(pgeUid, {
                    form: form.revwPrcssSrch.getValues(),
                    page: grid.revwPrcssList.page,
                    size: grid.revwPrcssList.size,
                });
            },
        }),
    };

    // Handlers
    const handler = {
        getRevwPrcssList: () => {
            form.revwPrcssSrch.handleSubmit(
                () => {
                    grid.revwPrcssList.setPage(0);
                    fetch.getRevwPrcssList.fetch(0);
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" });
                },
            )();
        },
        onSrchBtnClick: form.revwPrcssSrch.handleSubmit(
            () => {
                grid.revwPrcssList.setPage(0);
                fetch.getRevwPrcssList.fetch(0);
            },
            () => toast.showToast({ type: "warning", content: "msg.00002" }), // Data is invalid
        ),
    };

    // Render custom cells
    const render = {
        cell: {
            usrFdbckPrcssId: (props: any) => {
                const { rowValues } = props;
                const url = comnUtils.isEmpty(menuId)
                    ? `${URLS.ptli0707003s}`
                    : `/ptli/ptl/revwprcs/ptli0707002s`;

                return (
                    <Link to={`${url}/${rowValues.usrFdbckPrcssId}`}>
                        {rowValues.usrFdbckPrcssId}
                    </Link>
                );
            },
        },
    };

    // Load list when page mounts
    useEffect(() => {
        handler.getRevwPrcssList();
    }, []);

    return (
        <Page>
            {/* Search form */}
            <form>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.revwPrcssSrch.schema.usrFdbckPrcssId}></Group.Control>
                                <Group.Control {...form.revwPrcssSrch.schema.usrFdbckPrcssNm}></Group.Control>
                            </Group.Row>
                        </Group.Section>
                        <Layout direction="row">
                            <Layout.Left>
                                <Button
                                    role="reset"
                                    onClick={() => {
                                        form.revwPrcssSrch.reset();
                                    }}
                                ></Button>
                            </Layout.Left>
                            <Layout.Right>
                                <Button role={"search"} onClick={handler.onSrchBtnClick} />
                            </Layout.Right>
                        </Layout>
                    </Group.Body>
                </Group>
            </form>

            {/* Grid with list */}
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Grid
                            {...grid.revwPrcssList.grid}
                            data={fetch.getRevwPrcssList.data?.revwPrcssList}
                            render={render}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>

            {/* New button */}
            <Layout.Left>
                <Layout.Button
                    role={"new"}
                    onClick={() => {
                        navigate(URLS.ptli0707002s);
                    }}
                >
                    {t("B_NEW_$0", { 0: "Process" })}
                </Layout.Button>
            </Layout.Left>
        </Page>
    );
};
